import { getField, updateField } from "vuex-map-fields";
import punchCard from "@/services/punch.card";
import notification from "@/services/notification";
import _ from "lodash";

const GENERIC_ERROR_MSG = "Oops, something went wrong. Please try again."

export default {
  namespaced: true,
  state: {
    modalOpeningKey: 0,
    listFilters: {
      keyword: null,
      status: null,
      sort: null
    },
    errors: null,
    loading: false,
    list_view: "table",
    campaigns: [],
    pagination: {
      currentPage: 1,
      totalRows: 1,
      maxPage: 1,
      dataPerPage: 10
    },
    campaignDetails: null,
    campaignData: {
      status: "ACTIVE",
      active: "details",
      milestones: {
        details: 1,
        execution: 2,
        design: 3
      },
      designVisibility: {
        stamps: true,
        rewards: false,
        customize: false
      },
      details: {
        name: null,
        description: null,
        terms_condition: null,
        website_url: null
      },
      execution: {
        is_open_duration: false,
        date_start: null,
        date_end: null,
        publish_date_same_as_start_date: false,
        publishing_date: null,
        publishing_time: {
          HH: "",
          mm: ""
        },
        is_open_to_guest: false,
        target_audience: null,
        frequency: null
      },
      design: {
        number_of_stamps: 3,
        stamp_icon: null,
        stamp_preset: null,
        stamp_base64: false,
        milestones: [],
        customize: {
          campaign_card_color: "#FFFFFF",
          image: null,
          campaign_background_image: null,
          campaign_layout: 1
        }
      }
    },
    campaignDataUntouched: null,
    rewardsListLoading: false,
    punchCardRewardsList: [],
    rewardsListPagination: {
      current: 0,
      max_page: 1
    },
    uploadingImg: false,
    detailsLoading: false,
    isSaving: false
  },
  getters: {
    getField,
    currentStep: (state) => state.campaignData.milestones[state.campaignData.active],
    loading: (state) => state.loading,
    uploadingImg: (state) => state.uploadingImg,
    detailsLoading: (state) => state.detailsLoading,
    errors: (state) => state.errors,
    campaignDetails: (state) => state.campaignDetails,
    isSaving: (state) => state.isSaving
  },
  mutations: {
    updateField,
    INIT_CAMPAIGN_DATA(state) {
      state.campaignData = {
        ...state.campaignData,
        active: "details",
        designVisibility: {
          stamps: true,
          rewards: false,
          customize: false
        },
        details: {
          name: null,
          description: null,
          terms_condition: null,
          website_url: null
        },
        execution: {
          is_open_duration: false,
          date_start: null,
          date_end: null,
          publish_date_same_as_start_date: false,
          publishing_date: null,
          publishing_time: {
            HH: "",
            mm: ""
          },
          is_open_to_guest: false,
          target_audience: null,
          frequency: null
        },
        design: {
          number_of_stamps: 3,
          stamp_icon: null,
          stamp_preset: null,
          stamp_base64: false,
          milestones: [],
          customize: {
            campaign_card_color: "#FFFFFF",
            image: null,
            campaign_background_image: null,
            campaign_layout: 1
          }
        }
      }
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_ERRORS(state, errors) {
      state.errors = errors;
    },
    SET_CAMPAIGNS(state, campaigns) {
      state.campaigns = campaigns;
    },
    SET_PAGINATION(state, pagination) {
      Object.keys(pagination).forEach((key) => {
        state.pagination[key] = pagination[key];
      });
    },
    SET_REWARDS(state, list) {
      if (list.length > 0)
        list.forEach((reward) => state.punchCardRewardsList.push(reward));
      else
        state.punchCardRewardsList = list;
    },
    SET_REWARDS_LOADING(state, value) {
      state.rewardsListLoading = value;
    },
    SET_REWARDS_LIST_PAGINATION(state, pagination) {
      state.rewardsListPagination = pagination;
    },
    SET_UPLOADING_IMAGE(state, value) {
      state.uploadingImg = value;
    },
    SET_DETAILS_LOADING(state, value) {
      state.detailsLoading = value;
    },
    SET_CAMPAIGN_DETAILS(state, data) {
      state.campaignDetails = data;
    },
    SET_IS_SAVING(state, value = false) {
      state.isSaving = value;
    }
  },
  actions: {
    initStateLoadingAndErrors({ commit }) {
      commit("SET_ERRORS", null);
      commit("SET_LOADING", true);
    },
    async getCampaigns({ commit, dispatch }, payload) {
      dispatch("initStateLoadingAndErrors");
      commit("SET_CAMPAIGNS", []);

      try {
        const { campaigns, current_page, last_page, total } = await punchCard.getCampaigns(payload);
        commit("SET_CAMPAIGNS", campaigns);
        commit("SET_PAGINATION", { currentPage: current_page, maxPage: last_page, totalRows: total });
      } catch ({ errors }) {
        commit("SET_ERRORS", errors);
        notification.warning(_.get(errors, "message", GENERIC_ERROR_MSG));
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async getPunchCardRewardsList({ commit, dispatch }, payload) {
      dispatch("initStateLoadingAndErrors");
      commit("SET_REWARDS_LOADING", true);
      const { page } = payload || 1;

      if (page === 1)
        commit("SET_REWARDS", []);

      try {
        const { list, max_page } = await punchCard.getPunchCardRewardsList(payload);
        commit("SET_REWARDS_LIST_PAGINATION", { current: page, max_page });
        commit("SET_REWARDS", list);
      } catch ({ errors }) {
        commit("SET_ERRORS", errors);
        notification.warning(_.get(errors, "message", GENERIC_ERROR_MSG));
      } finally {
        commit("SET_LOADING", false);
        commit("SET_REWARDS_LOADING", false);
      }
    },
    async punchCardUploadImage({ commit }, payload) {
      commit("SET_ERRORS", null);
      commit("SET_UPLOADING_IMAGE", true);

      try {
        const { url } = await punchCard.punchCardUploadImage(payload);
        return url;
      } catch ({ errors }) {
        commit("SET_ERRORS", _.get(errors, "message", GENERIC_ERROR_MSG));
        notification.warning(_.get(errors, "message", GENERIC_ERROR_MSG));
      } finally {
        commit("SET_UPLOADING_IMAGE", false);
      }
    },
    async validateCampaignName({ commit, dispatch }, payload) {
      dispatch("initStateLoadingAndErrors");

      try {
        await punchCard.validateCampaignName(payload);
      } catch ({ errors }) {
        commit("SET_ERRORS", {
          name: _.get(errors, "message")
        });
        notification.warning(GENERIC_ERROR_MSG);
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async createPunchCardCampaign({ commit, dispatch }, payload) {
      dispatch("initStateLoadingAndErrors");

      try {
        const { message } = await punchCard.createPunchCardCampaign(payload);
        notification.success(message);
      } catch ({ errors }) {
        commit("SET_ERRORS", errors);
        notification.warning(_.get(errors, "message", GENERIC_ERROR_MSG));
      } finally {
        commit("SET_LOADING", false);
        commit("SET_IS_SAVING");
      }
    },
    async getPunchCardCampaignDetails({ commit }, uuid) {
      commit("SET_DETAILS_LOADING", true);
      commit("SET_ERRORS", null);
      commit("SET_CAMPAIGN_DETAILS", null);

      try {
        const { campaign } = await punchCard.getPunchCardCampaignDetails(uuid);
        commit("SET_CAMPAIGN_DETAILS", campaign);
      } catch ({ errors }) {
        commit("SET_ERRORS", errors);
        notification.warning(_.get(errors, "message", GENERIC_ERROR_MSG));
      } finally {
        commit("SET_DETAILS_LOADING", false);
      }
    },
    async updatePunchCardCampaign({ commit, dispatch }, { uuid, params }) {
      dispatch("initStateLoadingAndErrors");

      try {
        const { message } = await punchCard.updatePunchCardCampaign({ uuid, params });
        notification.success(message);
      } catch ({ errors }) {
        commit("SET_ERRORS", errors);
        notification.warning(_.get(errors, "message", GENERIC_ERROR_MSG));
      } finally {
        commit("SET_LOADING", false);
        commit("SET_IS_SAVING");
      }
    }
  }
};
